import { gql, useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { FolderId } from 'modules/domains/foldering';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import { CurrentSelection, SelectionTypes } from 'utils/contexts/selection';
import noop from 'utils/noop';
import { CampaignId } from '../domainTypes';

enum KnownMoveFailures {
  DestinationNotFoundFailure = 'DestinationNotFoundFailure',
  FolderIsAncestorOfDestinationFailure = 'FolderIsAncestorOfDestinationFailure',
  FolderNameConflictFailure = 'FolderNameConflictFailure',
  FolderNameNotFoundFailure = 'FolderNameNotFoundFailure',
  InternalError = 'InternalError',
  ParentNotFoundFailure = 'ParentNotFoundFailure',
  CampaignDoesNotExistFailure = 'CampaignDoesNotExistFailure',
}

type MoveError = {
  id: FolderId | CampaignId;
  name?: string;
  __typename: KnownMoveFailures;
};

type MovedSuccess = {
  id: FolderId;
  name: string;
};

type MoveFolderResponseData = {
  moveCampaignContentsAndFolders: {
    errors: MoveError[];
    movedContent: MovedSuccess[];
  };
};

type Variables = {
  destinationId: FolderId | null;
  folderIds: FolderId[];
  contentIds: CampaignId[];
};

const MoveCampaignsAndFoldersMutation = gql`
  mutation MoveCampaignsAndFolders(
    $contentIds: [Long!]!
    $destinationId: Long!
    $folderIds: [Long!]!
  ) {
    moveCampaignContentsAndFolders(
      destinationId: $destinationId
      folderIds: $folderIds
      contentIds: $contentIds
    ) {
      errors {
        ... on ContentsAndFoldersFailure {
          __typename
        }
      }
      movedContent {
        id
        name
      }
    }
  }
`;

type Config = {
  onComplete: () => void;
  onError: () => void;
  onSuccess: (data?: MoveFolderResponseData | null) => void;
  destinationName: string;
  itemsById: Record<FolderId | CampaignId, CurrentSelection>;
};
export function useMoveCampaignsAndFolders({
  onComplete = noop,
  onError = noop,
  onSuccess = noop,
  destinationName,
  itemsById = {},
}: Config) {
  const dispatch = useDispatch();
  const [moveCampaignsAndFolders, { called, loading, error, data }] = useMutation<
    MoveFolderResponseData,
    Variables
  >(MoveCampaignsAndFoldersMutation);
  useEffect(() => {
    // don't do anything until the mutation gets called
    if (!called) {
      return;
    }

    if (!loading && error) {
      const errorMessage = 'An unknown error occurred';
      dispatch(addNegativeToastWithErrorId(errorMessage, 'useMoveCampaignsAndFolders', error));
    }

    if (!loading && data?.moveCampaignContentsAndFolders) {
      const { errors, movedContent } = data.moveCampaignContentsAndFolders;

      errors.forEach((error) => {
        switch (error.__typename) {
          case KnownMoveFailures.DestinationNotFoundFailure: {
            const errorMessage = (
              <ResponseWrapper>
                <NamedFolder name={itemsById?.[error?.id]?.name || ''} /> folder can not be moved as{' '}
                <NamedFolder name={destinationName} /> does not exist.
              </ResponseWrapper>
            );

            dispatch(
              addNegativeToastWithErrorId(errorMessage, 'useMoveCampaignsAndFolders', error),
            );
            break;
          }

          case KnownMoveFailures.FolderNameConflictFailure: {
            const errorMessage = (
              <ResponseWrapper>
                The folder can not be moved into <NamedFolder name={destinationName} /> as a folder
                with that name already exists. This could also be because the folder is already a
                child folder of <NamedFolder name={destinationName} />.
              </ResponseWrapper>
            );

            dispatch(
              addNegativeToastWithErrorId(errorMessage, 'useMoveCampaignsAndFolders', error),
            );
            break;
          }

          case KnownMoveFailures.FolderIsAncestorOfDestinationFailure: {
            const name = itemsById?.[error?.id]?.name || '';

            const errorMessage = (
              <ResponseWrapper>
                <NamedFolder name={name} /> folder can not be moved into <NamedFolder name={name} />{' '}
                folder or one of its child folders.
              </ResponseWrapper>
            );

            dispatch(
              addNegativeToastWithErrorId(errorMessage, 'useMoveCampaignsAndFolders', error),
            );
            break;
          }

          case KnownMoveFailures.FolderNameNotFoundFailure: {
            const errorMessage = 'Could not move: FolderNameConflictFailure';
            dispatch(
              addNegativeToastWithErrorId(errorMessage, 'useMoveCampaignsAndFolders', error),
            );
            break;
          }

          case KnownMoveFailures.ParentNotFoundFailure: {
            const errorMessage = 'Could not move: ParentNotFoundFailure';
            dispatch(
              addNegativeToastWithErrorId(errorMessage, 'useMoveCampaignsAndFolders', error),
            );

            break;
          }

          case KnownMoveFailures.CampaignDoesNotExistFailure: {
            const errorMessage = 'Could not move: CampaignDoesNotExistFailure';
            dispatch(
              addNegativeToastWithErrorId(errorMessage, 'useMoveCampaignsAndFolders', error),
            );
            break;
          }

          default: {
            const name = itemsById?.[error?.id]?.name || '';
            const errorMessage = (
              <ResponseWrapper>
                <NamedFolder name={name} /> folder could not be moved.
              </ResponseWrapper>
            );

            dispatch(
              addNegativeToastWithErrorId(errorMessage, 'useMoveCampaignsAndFolders', error),
            );
          }
        }
        onError();
      });

      movedContent.forEach((content: MovedSuccess) => {
        switch (itemsById[content.id].type) {
          case SelectionTypes.Content: {
            dispatch(
              addPositiveToast(
                <ResponseWrapper>
                  <strong>Campaign {content.name}</strong> was successfully moved into the{' '}
                  <NamedFolder name={destinationName} /> folder.
                </ResponseWrapper>,
              ),
            );
            break;
          }

          case SelectionTypes.Folder: {
            dispatch(
              addPositiveToast(
                <ResponseWrapper>
                  <NamedFolder name={content.name} /> was successfully moved into the{' '}
                  <NamedFolder name={destinationName} /> folder.
                </ResponseWrapper>,
              ),
            );
            break;
          }

          default: {
            console.error('Moved an unallowed content item: possible type mismatch'); // eslint-disable-line
          }
        }

        onSuccess(data);
      });

      // call the complete callback
      onComplete();
    }
  }, [loading]);

  return [moveCampaignsAndFolders, data] as const;
}
